import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid as Grid2,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  useTheme,
  Tabs,
  Tab,
  Stack,
  Chip,
} from "@mui/material";
import Breadcrumb from "../../../../Components/BreadCrumb";
import dayjs, { Dayjs } from "dayjs";
import { styled } from "@mui/material/styles";
import { FaFilter, FaEye, FaXmark } from "react-icons/fa6";
import ExportButton from "../../../../Components/ExportButton";
import { useProductDenominationLabels } from "../../../../Components/ProductDenominationFilter";
import RefreshButton from "../../../../Components/RefreshButton";
import {
  useExportReportMutation,
  useGetSalesReportMutation,
  useGetRabbitMQLogsMutation,
} from "../../../../Services/ReportsService";
import { SalesDataArray } from "../../../../Helpers/Interfaces/SalesReports";
import ITopupDateRangePicker from "../../../../Components/ITopupDateRangePicker";
import Utils from "../../../../Helpers/Utils";
import { showSnackbar } from "../../../../Redux/Slices/NotificationSlice";
import ApiPath from "../../../../Services/ConstantApi";
import downloadReport, {
  datedFileName,
} from "../../../../Helpers/Export/downloadReport";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SalesReportsSlidingFilter, {
  SalesFilters,
} from "../../../../Components/SlidingFilter/SalesReportsSlidingFilter";
import AppliedFilter from "../../../../Components/AppliedFilter";
import { CircularProgress } from "@mui/material";

export interface Filters {
  dateRange: [Dayjs | null, Dayjs | null];
  paymentStatus: string;
  paymentMethod: string;
  productType: string;
}

// Combined state interface
interface CombinedState {
  page: number;
  pageSize: number;
  startDate?: string;
  endDate?: string;
  productType: string;
  orderNumber: string;
  batchNumber: string;
  customerName?: string;
  supplier?: string;
  subSupplier?: string;
  productName?: string;
  /** Chosen from the Product dropdown. */
  productId?: string;
  /** The SKU of a denomination of that product. Depends on productId. */
  productDenominationsku?: string;
  callResult?: string;
  /** "" = everything, "purchased", or "no-purchase". Driven by the chip row. */
  rowSource?: string;
}

export const FilterGrid = styled(Grid2)`
  &.filter-grid {
    .MuiInputBase-root {
      height: 35px;
      min-height: 35px;

      .MuiOutlinedInput-input {
        padding: 5px 14px;
      }

      .MuiSelect-select {
        padding: 5px 14px;
        font-size: 12px;
      }

      .MuiInputLabel-root {
        transform: translate(14px, 8px) scale(1);
        &.MuiInputLabel-shrink {
          transform: translate(14px, -9px) scale(0.75);
        }
      }
    }

    .MuiFormLabel-root {
      top: -3px;
    }
  }
`;

const SalesReports: React.FC = () => {
  const today = dayjs();
  const todayStr = today.format("YYYY-MM-DD");
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filters, setFilters] = React.useState<Filters>({
    dateRange: [today, today],
    paymentStatus: "all",
    paymentMethod: "all",
    productType: "",
  });

  // Sliding filter local state, aligned to SalesReportsSlidingFilter interface
  const [salesFilters, setSalesFilters] = useState<SalesFilters>({
    productType: "All",
    orderNumber: "",
    startDate: todayStr,
    endDate: todayStr,
    batchNumber: "",
    customerName: "",
    supplier: "All",
    subSupplier: "All",
    productName: "All",
    callResult: "All",
  });

  const [combinedState, setCombinedState] = useState<CombinedState>({
    // Pagination
    page: 0,
    pageSize: 10,
    startDate: todayStr,
    endDate: todayStr,
    orderNumber: "",
    productType: "All",
    batchNumber: "",
    customerName: "",
    supplier: "",
    subSupplier: "",
    productName: "",
    productId: "",
    productDenominationsku: "",
    callResult: "",
    rowSource: "",
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const useInfo = useSelector((state: any) => state.AuthSlice.adminAuthUser);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedOrder, setSelectedOrder] =
    React.useState<SalesDataArray | null>(null);
  const [dialogType, setDialogType] = React.useState<
    "products" | "invoice" | "api" | null
  >(null);
  const [getOrderListTrigger, { data, isLoading, error }] =
    useGetSalesReportMutation();
  const [
    getRabbitMQLogsTrigger,
    {
      data: rabbitMQLogsData,
      isLoading: rabbitMQLogsLoading,
      error: rabbitMQLogsError,
    },
  ] = useGetRabbitMQLogsMutation();
  const [exportReport] = useExportReportMutation();
  const [exporting, setExporting] = useState(false);
  /*
   * The supplier Reconciliation button used to sit beside Export.
   *
   * Hidden on Vijay's instruction, 30 Sep 2026, after testing the new
   * Export. It was removed once, put back while he decided, and is now off
   * for good unless he asks again.
   *
   * Nothing underneath it was deleted. Components/SupplierReconciliationExport
   * and GET /reports/supplier-reconciliation are both intact and working:
   * the report still exists, there is simply no way into it from here.
   * Bringing it back is the button and the modal, nothing more.
   *
   * Kept rather than deleted because the two answer different questions.
   * Export gives the rows on this screen -- what we sold. Reconciliation
   * checks a supplier's INVOICE against what they actually delivered, which
   * is not in this table at all, so Export cannot produce it however it is
   * filtered.
   */

  // Reload without losing the filters, page and sort already set up.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Dayjs | null>(null);
  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
  };
  const [listLoading, setListLoading] = useState(false);
  const [apiTab, setApiTab] = useState(0);
  /**
   * Which supplier call this modal is showing.
   *
   * Every tab used to read data[0] -- the most recent call, since the API sorts
   * newest first -- and there was no way to reach any of the others. An order
   * that talked to a supplier more than once showed one exchange and gave no
   * sign the rest existed: 259 orders are in that state, one of them with 15
   * calls. On a failed delivery the retries are usually the point.
   *
   * Defaults to 0, which stays the newest call, so the modal opens on what it
   * always showed.
   */
  const [apiCallIndex, setApiCallIndex] = useState(0);

  // Build API filters based on applied combinedState
  /*
   * The filters the screen currently has, in the shape the API takes.
   *
   * Shared by the list below and the export, because they MUST be the same
   * set. They were built separately and had already drifted: the export left
   * out supplier, sub-supplier, product name, call result and row source, so
   * a person who filtered by supplier and pressed Export got a file covering
   * every supplier -- with nothing on screen to say so.
   */
  const buildFilters = useCallback(() => {
    const dynamicFilters: { key: string; value: any }[] = [];

    if (combinedState.orderNumber) {
        dynamicFilters.push({
          key: "orderNumber",
          value: combinedState.orderNumber,
        });
      }
      if (combinedState.customerName) {
        dynamicFilters.push({
          key: "customer",
          value: combinedState.customerName,
        });
      }
      if (combinedState.startDate) {
        dynamicFilters.push({
          key: "startDate",
          value: combinedState.startDate,
        });
      }
      if (combinedState.endDate) {
        // ensure endDate includes end-of-day if needed by API; keep raw if backend expects date only
        dynamicFilters.push({ key: "endDate", value: combinedState.endDate });
      }
      if (combinedState.productType && combinedState.productType !== "All") {
        dynamicFilters.push({
          key: "productType",
          value: combinedState.productType,
        });
      }
      if (combinedState.batchNumber) {
        dynamicFilters.push({ key: "batch", value: combinedState.batchNumber });
      }
      if (combinedState.supplier) {
        dynamicFilters.push({ key: "supplier", value: combinedState.supplier });
      }
      if (combinedState.subSupplier) {
        dynamicFilters.push({
          key: "subSupplier",
          value: combinedState.subSupplier,
        });
      }
      if (combinedState.productName) {
        dynamicFilters.push({
          key: "productName",
          value: combinedState.productName,
        });
      }
      // Product and denomination, chosen from the dropdowns.
      if (combinedState.productId) {
        dynamicFilters.push({
          key: "productId",
          value: combinedState.productId,
        });
      }
      if (combinedState.productDenominationsku) {
        dynamicFilters.push({
          key: "productDenominationsku",
          value: combinedState.productDenominationsku,
        });
      }
      if (combinedState.callResult) {
        dynamicFilters.push({
          key: "callResult",
          value: combinedState.callResult,
        });
      }
      if (combinedState.rowSource) {
        dynamicFilters.push({
          key: "rowSource",
          value: combinedState.rowSource,
        });
      }

    return dynamicFilters;
  }, [
    combinedState.orderNumber,
    combinedState.customerName,
    combinedState.startDate,
    combinedState.endDate,
    combinedState.productType,
    combinedState.batchNumber,
    combinedState.supplier,
    combinedState.subSupplier,
    combinedState.productName,
    combinedState.productId,
    combinedState.productDenominationsku,
    combinedState.callResult,
    combinedState.rowSource,
  ]);

  useEffect(() => {
    const fetchList = async () => {
      const filterReq: any = {
        filters: buildFilters(),
        page: page + 1,
        pageSize: rowsPerPage,
      };

      setListLoading(true);
      try {
        await getOrderListTrigger(filterReq).unwrap();
      } catch (e) {
      } finally {
        setListLoading(false);
        setRefreshing(false);
        setLastUpdated(dayjs());
      }
    };

    fetchList();
  }, [
    refreshKey,
    page,
    rowsPerPage,
    combinedState.orderNumber,
    combinedState.customerName,
    combinedState.startDate,
    combinedState.endDate,
    combinedState.productType,
    combinedState.batchNumber,
    combinedState.supplier,
    combinedState.subSupplier,
    combinedState.productName,
    combinedState.productId,
    combinedState.productDenominationsku,
    combinedState.callResult,
    combinedState.rowSource,
  ]);
  // Update combined state helper
  const updateCombinedState = useCallback((updates: Partial<CombinedState>) => {
    setCombinedState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  /*
   * Export what is on screen.
   *
   * Sends the same filters the table was just filled with, to the same
   * endpoint, and the server runs the same query without pagination. There
   * is no second definition of "the report" to go stale.
   *
   * What this replaces: a call to /purchase-orders/export with
   * isExport=true, which never produced a file at all. It wrote a 'pending'
   * row to reportexportlogs, answered 202 "you will be notified", and
   * nothing anywhere processes those rows -- six of them are still sitting
   * there. The screen showed that 202 message as a success, so Export looked
   * like it had worked every time.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportReport({
        url: ApiPath.getSalesReport,
        body: { filters: buildFilters() },
      }).unwrap();

      const result = await downloadReport(blob, datedFileName("sales-report"));
      if (!result.ok) {
        dispatch(
          showSnackbar({ message: result.message, severity: "warning" }),
        );
      }
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message:
            err?.data?.message || "The export could not be produced.",
          severity: "error",
        }),
      );
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(0);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof Filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCloseDialog = () => {
    setSelectedOrder(null);
    setDialogType(null);
  };

  /**
   * What to say when an order has no supplier call recorded.
   *
   * The three tabs each said "No overview / request / response data
   * available", which reads as a fault in this screen. It is not: some orders
   * genuinely have no call stored, because API logging did not cover their
   * supplier when they were placed -- 127 of 682 purchase orders are in that
   * state, all of them older, e.g. DTONE stopped appearing after 21 Apr 2026.
   * Saying so plainly stops anyone hunting for a bug that is not there.
   */
  const NoApiCallRecorded = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" color="text.secondary">
        No supplier API call was recorded for this order.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The purchase completed, but its request and response were not stored —
        usually because API logging did not yet cover this supplier when the
        order was placed. Nothing is missing from the order itself.
      </Typography>
    </Box>
  );

  const handleViewApi = (order: any) => {
    setSelectedOrder(order);
    setDialogType("api");
    setApiCallIndex(0);
    setApiTab(0);
    // Trigger RabbitMQ Logs API
    getRabbitMQLogsTrigger({
      orderNumber: order.orderNumber,
      batch: order.batch,
      // The line that was clicked. Without it a row with no batch -- a bus
      // booking, say -- widened to the whole order and listed other items'
      // suppliers.
      orderItemId: order.orderItemId ?? order._id,
    });
  };

  const isDateChanged = Utils.isDateChanged(filters.dateRange);

  const handleSlidingFilterChange = (
    filterName: keyof SalesFilters,
    value: any,
  ) => {
    setSalesFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  /** The panel's dropdowns use "All" to mean "no filter". */
  const notAll = (v?: string) => (!v || v === "All" ? "" : v);

  const handleApplyFilters = (filtersToApply: SalesFilters) => {
    // Persist in local sliding filter state
    setSalesFilters(filtersToApply);

    // Update the combined state with applied filters (used for fetching)
    updateCombinedState({
      page: 0,
      productType: filtersToApply.productType || "All",
      orderNumber: filtersToApply.orderNumber || "",
      startDate: filtersToApply.startDate || ("" as any),
      endDate: filtersToApply.endDate || ("" as any),
      batchNumber: filtersToApply.batchNumber || "",
      customerName: (filtersToApply as any).customerName || "",
      // "All" is the panel's way of saying "no filter", so it must not travel
      // to the server as a value to match on.
      supplier: notAll(filtersToApply.supplier),
      subSupplier: notAll(filtersToApply.subSupplier),
      productName: notAll(filtersToApply.productName),
      productId: filtersToApply.productId || "",
      // A denomination only means anything alongside its product. Without
      // one it filters on a SKU nothing on the panel still shows.
      productDenominationsku: filtersToApply.productId
        ? filtersToApply.productDenominationsku || ""
        : "",
      callResult: notAll(filtersToApply.callResult),
    });

    setPage(0);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    // Reset old filters block (kept for dateRange default UI, if used elsewhere)
    setFilters({
      dateRange: [null, null],
      paymentStatus: "all",
      paymentMethod: "all",
      productType: "",
    });

    // Reset sliding filter state to defaults
    setSalesFilters({
      supplier: "All",
      subSupplier: "All",
      productName: "All",
      productId: "",
      productDenominationsku: "",
      callResult: "All",
      productType: "All",
      orderNumber: "",
      startDate: "",
      endDate: "",
      batchNumber: "",
      customerName: "",
    });

    // Clear the combined state used for API
    updateCombinedState({
      page: 0,
      orderNumber: "",
      productType: "All",
      startDate: "",
      endDate: "",
      batchNumber: "",
      customerName: "",
      supplier: "",
      subSupplier: "",
      productName: "",
      productId: "",
      productDenominationsku: "",
      callResult: "",
      rowSource: "",
    });
    setPage(0);
  };

  // Names for the Product and Denomination chips. Same cached query the
  // dropdowns use, so it costs no request.
  const { productLabel, denominationLabel } = useProductDenominationLabels(
    combinedState.productId,
    combinedState.productDenominationsku,
  );

  const appliedFiltersForChips = {
    productType:
      combinedState.productType === "All" ? "" : combinedState.productType,
    orderNumber: combinedState.orderNumber || "",
    customerName: combinedState.customerName || "",
    startDate: (combinedState as any).startDate || "",
    endDate: (combinedState as any).endDate || "",
    batchNumber: combinedState.batchNumber || "",
    supplier: combinedState.supplier || "",
    subSupplier: combinedState.subSupplier || "",
    productName: combinedState.productName || "",
    callResult: combinedState.callResult || "",
    productId: productLabel,
    productDenominationsku: denominationLabel,
  };

  // Clear a single filter from chips and keep both combinedState and sliding filters in sync
  const handleClearSingleFilter = (filterKey: string) => {
    const updates: Partial<CombinedState> = { page: 0 };

    if (filterKey === "productType") {
      updates.productType = "All";
      setSalesFilters((prev) => ({ ...prev, productType: "All" }));
    } else if (filterKey === "orderNumber") {
      updates.orderNumber = "";
      setSalesFilters((prev) => ({ ...prev, orderNumber: "" }));
    } else if (filterKey === "customerName") {
      updates.customerName = "";
      setSalesFilters((prev) => ({ ...prev, customerName: "" }));
    } else if (filterKey === "startDate") {
      (updates as any).startDate = "";
      setSalesFilters((prev) => ({ ...prev, startDate: "" }));
    } else if (filterKey === "endDate") {
      (updates as any).endDate = "";
      setSalesFilters((prev) => ({ ...prev, endDate: "" }));
    } else if (filterKey === "batchNumber") {
      (updates as any).batchNumber = "";
      setSalesFilters((prev) => ({ ...prev, batchNumber: "" }));
    } else if (
      ["supplier", "subSupplier", "productName", "callResult"].includes(
        filterKey,
      )
    ) {
      (updates as any)[filterKey] = "";
      setSalesFilters((prev) => ({ ...prev, [filterKey]: "All" }));
    }

    /*
     * Clearing the product clears the denomination with it. A denomination
     * on its own filters on a SKU with no product beside it -- invisible on
     * the panel, and matching nothing.
     */
    if (filterKey === "productId") {
      (updates as any).productId = "";
      (updates as any).productDenominationsku = "";
      setSalesFilters((prev) => ({
        ...prev,
        productId: "",
        productDenominationsku: "",
      }));
    }
    if (filterKey === "productDenominationsku") {
      (updates as any).productDenominationsku = "";
      setSalesFilters((prev) => ({ ...prev, productDenominationsku: "" }));
    }

    // Update combined state for API calls
    updateCombinedState(updates);
  };

  return (
    <Box
      sx={{
        p: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Breadcrumb
          name="API Transactions"
          items={[{ label: "Reports" }, { label: "API Transactions" }]}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <AppliedFilter
            filters={appliedFiltersForChips}
            onClearFilter={handleClearSingleFilter}
            onClearAll={handleClearFilters}
            filterLabels={{
              productId: "Product",
              productDenominationsku: "Denomination",
              productType: "Product Type",
              orderNumber: "Order Number",
              customerName: "Customer",
              startDate: "Start Date",
              endDate: "End Date",
              batchNumber: "Batch Number",
              supplier: "Supplier",
              subSupplier: "Sub Supplier",
              productName: "Product",
              callResult: "Call Result",
            }}
            customFormatters={{
              startDate: (v: string) =>
                v ? dayjs(v).format("YYYY-MM-DD") : "",
              endDate: (v: string) =>
                v ? dayjs(v).format("YYYY-MM-DD") : "",
              productType: (v: string) =>
                v
                  ? v
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")
                  : "",
              customerName: (v: string) => (v ? v : ""),
              batchNumber: (v: string) => (v ? v : ""),
              orderNumber: (v: string) => (v ? v : ""),
              supplier: (v: string) => (v ? v : ""),
              subSupplier: (v: string) => (v ? v : ""),
              productName: (v: string) => (v ? v : ""),
              callResult: (v: string) =>
                v ? v.charAt(0).toUpperCase() + v.slice(1) : "",
            }}
          />
          <RefreshButton
            onRefresh={handleRefresh}
            refreshing={refreshing}
            lastUpdated={lastUpdated}
          />

          {/*
            Export takes the filters already on the screen. No modal, nothing
            to choose again -- what is in the table is what lands in the file.
          */}
          <ExportButton onExport={handleExport} exporting={exporting} />

          <Button
            color="secondary"
            size={"medium"}
            sx={{
              height: 34,
              textTransform: "capitalize",
              boxShadow: theme.shadows[1],
            }}
            variant="outlined"
            startIcon={<FaFilter size={16} />}
            onClick={() => setFilterOpen(true)}
          >
            &nbsp;<span>Apply Filters</span>
          </Button>
        </Box>
      </Box>

      {/*
        Which half of the screen you are looking at.

        This page lists two different things: purchases that completed, and
        supplier calls that never became a purchase. The second kind has no
        batch, no pin and no supplier discount, so those columns are blank on
        them -- and four of the filters (Batch, Sub Supplier, and the two
        discount ranges) can only answer for the first kind, which means using
        one silently removes the second. Making the split a visible control
        explains both, and gives anyone who wants the old purchase-only report
        a single click to get it.
      */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {[
          { value: "", label: "All" },
          { value: "purchased", label: "Purchased" },
          { value: "no-purchase", label: "No purchase" },
        ].map((option) => (
          <Chip
            key={option.value || "all"}
            label={option.label}
            size="small"
            color={
              (combinedState.rowSource || "") === option.value
                ? "primary"
                : "default"
            }
            variant={
              (combinedState.rowSource || "") === option.value
                ? "filled"
                : "outlined"
            }
            onClick={() => {
              setPage(0);
              updateCombinedState({ rowSource: option.value });
            }}
            sx={{ cursor: "pointer" }}
          />
        ))}
      </Box>

      {/* Sales Table area: make table scrollable while keeping pagination fixed */}
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
          <Table
            sx={{
              tableLayout: "fixed",
              width: "100%",
              "& .MuiTableCell-root": {
                padding: "8px 12px",
                fontSize: "14px",
                borderBottom: "1px solid #e0e0e0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>#</TableCell>
                <TableCell sx={{ width: 120, fontWeight: "bold" }}>
                  Order Number
                </TableCell>
                {/* <TableCell sx={{ width: 120, fontWeight: "bold" }}>
                Order Item Number
              </TableCell> */}
                <TableCell sx={{ width: 180, fontWeight: "bold" }}>
                  Customer
                </TableCell>
                <TableCell sx={{ width: 120, fontWeight: "bold" }}>
                  Batch
                </TableCell>
                {/*
                  One value per column.
                  These four headings used to be two, each packing two
                  different fields either side of a slash -- "Product / P.D.
                  SKU" and "Supplier Id / Sub Supplier Id" -- which made them
                  unreadable at a glance and impossible to scan down.
                */}
                <TableCell sx={{ width: 180, fontWeight: "bold" }}>
                  Product Name
                </TableCell>
                <TableCell sx={{ width: 140, fontWeight: "bold" }}>
                  P.D. SKU
                </TableCell>
                <TableCell
                  sx={{ width: 120, textAlign: "center", fontWeight: "bold" }}
                >
                  Denomination
                </TableCell>
                <TableCell sx={{ width: 130, fontWeight: "bold" }}>
                  Supplier Id
                </TableCell>
                <TableCell sx={{ width: 150, fontWeight: "bold" }}>
                  Sub Supplier Id
                </TableCell>
                <TableCell sx={{ width: 160, fontWeight: "bold" }}>
                  Supplier Product
                </TableCell>
                <TableCell sx={{ width: 140, fontWeight: "bold" }}>
                  Supplier Denom.
                </TableCell>
                <TableCell sx={{ width: 130, fontWeight: "bold" }}>
                  Product Type
                </TableCell>
                <TableCell
                  sx={{ width: 90, textAlign: "center", fontWeight: "bold" }}
                >
                  Qty
                </TableCell>
                <TableCell
                  sx={{ width: 150, textAlign: "center", fontWeight: "bold" }}
                >
                  Amount
                </TableCell>
                <TableCell sx={{ width: 160, fontWeight: "bold" }}>
                  Date
                </TableCell>
                <TableCell
                  sx={{ width: 130, textAlign: "center", fontWeight: "bold" }}
                >
                  Customer Discount
                </TableCell>
                <TableCell
                  sx={{ width: 100, textAlign: "center", fontWeight: "bold" }}
                >
                  Supplier Discount
                </TableCell>
                <TableCell sx={{ width: 120, fontWeight: "bold" }}>
                  Pin Status
                </TableCell>
                <TableCell sx={{ width: 120, fontWeight: "bold" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(listLoading || isLoading) &&
                [...Array(10)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(19)].map((_, colIndex) => (
                      <TableCell key={colIndex}>
                        <Skeleton
                          variant="text"
                          width={colIndex === 0 ? 30 : "80%"}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!(listLoading || isLoading) &&
                (data as any)?.status === "error" &&
                (data as any)?.message && (
                  <TableRow>
                    <TableCell colSpan={19} align="center">
                      {(data as any).message}
                    </TableCell>
                  </TableRow>
                )}

              {!(listLoading || isLoading) &&
                (!data?.data || data?.data.length === 0) &&
                !(data as any)?.message && (
                  <TableRow>
                    <TableCell colSpan={19} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}

              {!(listLoading || isLoading) &&
                data?.data?.map((report: any, index: number) => (
                  <TableRow key={report._id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    <TableCell sx={{ width: 120 }}>
                      {report?.orderNumber}
                    </TableCell>
                    {/* <TableCell sx={{ width: 120 }}>
                    {report?.orderNumber}
                  </TableCell> */}
                    <TableCell sx={{ width: 180 }}>
                      {report?.userId
                        ? `${report.userId.firstName || ""} ${
                            report.userId.lastName || ""
                          }`
                        : "N/A"}
                      <br />
                      <small style={{ color: "gray", fontSize: "12px" }}>
                        {report?.userId?.email || "N/A"}
                      </small>
                    </TableCell>
                    <TableCell sx={{ width: 120 }}>{report?.batch}</TableCell>

                    {/* Product Name */}
                    <TableCell sx={{ width: 180 }}>
                      {report?.productType === "travel"
                        ? report?.product_instructions ||
                          report?.productName ||
                          "Travel Booking"
                        : report?.productName || "—"}
                    </TableCell>

                    {/* P.D. SKU -- our own denomination code for the line */}
                    <TableCell sx={{ width: 140 }}>
                      {report?.productDenomination || "—"}
                    </TableCell>

                    {/* Denomination */}
                    <TableCell align="center" sx={{ width: 120 }}>
                      {report?.productType === "cards"
                        ? report?.productId?.productDenominations?.[0]
                            ?.denomination ||
                          report?.customerOriginalPrice ||
                          report?.denomination ||
                          "—"
                        : report?.productType === "phone-refil"
                          ? report?.productId?.productDenominations?.[0]
                              ?.denomination || "—"
                          : "—"}
                    </TableCell>

                    {/* Supplier Id */}
                    <TableCell sx={{ width: 130 }}>
                      {report?.supplierId || "—"}
                    </TableCell>

                    {/* Sub Supplier Id */}
                    <TableCell sx={{ width: 150 }}>
                      {report?.subSupplierId || "—"}
                    </TableCell>

                    {/* Supplier Product -- the provider on the supplier's side */}
                    <TableCell sx={{ width: 160 }}>
                      {report?.supplierProviderName ||
                        (report?.productType === "travel"
                          ? report?.supplierId
                          : "") ||
                        "—"}
                    </TableCell>

                    {/* Supplier Denom. */}
                    <TableCell sx={{ width: 140 }}>
                      {/*
                        Falls back to the supplier's own code for the
                        denomination.
                        
                        DTone never writes supplierProductDenomination, so
                        all 37 of its refills showed a dash here -- while
                        supplerProductDenominationsku ("18922") sat on every
                        one of them. That code IS how the supplier names
                        that denomination, so it is the honest answer rather
                        than nothing.
                        
                        Travel still shows a dash, correctly: a bus seat has
                        no denomination and no supplier code for one.
                      */}
                      {report?.supplierProductDenomination ||
                        report?.supplerProductDenominationsku ||
                        "—"}
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      {report?.productType}
                    </TableCell>

                    <TableCell sx={{ width: 90, textAlign: "center" }}>
                      {report?.quantity || 1}
                    </TableCell>

                    <TableCell sx={{ width: 150, textAlign: "center" }}>
                      {Utils.formatEuro(
                        report?.productType === "cards"
                          ? report?.price
                          : report?.total_amount,
                      )}
                    </TableCell>

                    <TableCell sx={{ width: 160 }}>
                      {dayjs(report.createdAt).format("DD.MM.YYYY, HH.mm.ss")}
                    </TableCell>

                    {/* Customer Discount */}
                    <TableCell sx={{ width: 130, textAlign: "center" }}>
                      {report?.hasCustomerDiscount ? (
                        <>
                          <span style={{ fontWeight: 500 }}>
                            {report.customerDiscountValue}
                            {report.customerDiscountType === "percentage"
                              ? "%"
                              : " €"}
                          </span>
                          <br />
                          <small style={{ color: "#6b7280" }}>
                            {Utils.formatEuro(
                              (report.customerDiscountAmount || 0).toFixed(2),
                            )}
                          </small>
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    {/* Supplier Discount */}
                    <TableCell sx={{ width: 100, textAlign: "center" }}>
                      {report?.productType === "cards" ||
                      report?.productType === "phone-refil"
                        ? `${report?.supplierDiscount || 0}${report?.supplierDiscountType === "flat" ? " €" : "%"}`
                        : "-"}
                    </TableCell>

                    <TableCell sx={{ width: 120 }}>
                      {report?.productType === "cards"
                        ? report?.pinStatus === "assigned"
                          ? "Sold"
                          : report?.pinStatus === "unassigned"
                            ? "Unsold"
                            : /* The row came from a supplier call that never
                                 produced a purchase -- not an unsold pin, no
                                 pin at all. Saying "Unsold" or "N/A" here
                                 would hide the very thing the row is for. */
                              report?.pinStatus === "not-purchased"
                              ? "Not purchased"
                              : "N/A"
                        : report?.productType === "travel"
                          ? report?.status || "N/A"
                          : "N/A"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        sx={{ color: theme.palette.error.main }}
                        onClick={() => handleViewApi(report)}
                      >
                        <FaEye />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

              {!isLoading && error && (error as any)?.data?.message && (
                <TableRow>
                  <TableCell colSpan={19} align="center">
                    {(error as any).data.message}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ background: theme.palette.background.default }}>
          <TablePagination
            showFirstButton
            showLastButton
            component="div"
            count={data && data?.totalRecords ? data?.totalRecords : 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      </Box>
      <SalesReportsSlidingFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        filters={salesFilters}
        onFilterChange={handleSlidingFilterChange}
      />

      <Dialog
        open={!!selectedOrder}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">
              {dialogType === "api"
                ? "API Transaction"
                : dialogType === "products"
                  ? "Order Products"
                  : "Invoice Details"}
              {selectedOrder?.orderNumber
                ? ` - ${selectedOrder.orderNumber}`
                : ""}
            </Typography>
            <Box>
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ color: "text.secondary" }}
              >
                <FaXmark />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {dialogType === "products" ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody></TableBody>
            </Table>
          ) : (
            <Box>
              {/* One row per call, so the retries are reachable. Hidden when
                  there is only one, which is the common case and needs no
                  chooser. */}
              {(rabbitMQLogsData?.data?.length ?? 0) > 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {rabbitMQLogsData.data.length} supplier calls on this order
                    &mdash; newest first
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                    {rabbitMQLogsData.data.map((call: any, i: number) => {
                      const failed =
                        String(call?.status || "").toLowerCase() === "failed" ||
                        (typeof call?.responseStatus === "number" &&
                          call.responseStatus >= 400);
                      return (
                        <Chip
                          key={call?.requestId || i}
                          size="small"
                          label={`${i + 1}. ${call?.supplier || "supplier"} ${
                            failed ? "✕" : "✓"
                          }`}
                          onClick={() => setApiCallIndex(i)}
                          color={
                            i === apiCallIndex
                              ? failed
                                ? "error"
                                : "success"
                              : "default"
                          }
                          variant={i === apiCallIndex ? "filled" : "outlined"}
                          sx={{ fontSize: "0.7rem", cursor: "pointer" }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <Tabs
                value={apiTab}
                onChange={(_, v) => setApiTab(v)}
                sx={{ mb: 1 }}
              >
                <Tab label="OVERVIEW" />
                <Tab label="API Request" />
                <Tab label="API Response" />
              </Tabs>

              {apiTab === 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Overview
                  </Typography>
                  {rabbitMQLogsLoading ? (
                    <CircularProgress size={24} />
                  ) : rabbitMQLogsData?.data?.[apiCallIndex] ? (
                    <TableContainer>
                      <Table>
                        <TableBody>
                          {/* The reason a call failed is why anyone opens this
                              screen, and it was the one thing not on it. The
                              order detail panel had it; this did not, so the
                              same log read here said "failed" and no more.
                              Shown first, and only when there is one. */}
                          {rabbitMQLogsData.data[apiCallIndex].errorMessage && (
                            <TableRow>
                              <TableCell
                                sx={{ fontWeight: "bold", width: "30%", color: "error.main" }}
                              >
                                Failure Reason
                              </TableCell>
                              <TableCell sx={{ color: "error.main" }}>
                                {rabbitMQLogsData.data[apiCallIndex].errorMessage}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                              Endpoint
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                              {rabbitMQLogsData.data[apiCallIndex].apiEndpoint ||
                                rabbitMQLogsData.data[apiCallIndex].url ||
                                "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                              Method
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].method || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                              HTTP Status
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].responseStatus ?? "N/A"}
                            </TableCell>
                          </TableRow>
                          {/* Which line of the order this call was for. An
                              order with several items can hit several
                              providers, and without it a supplier name says
                              nothing about which product it was buying. */}
                          {rabbitMQLogsData.data[apiCallIndex].item?.productName && (
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                                Order Item
                              </TableCell>
                              <TableCell>
                                {/* item is an object, not a string -- rendering
                                    it directly would throw. Same shape the
                                    order detail panel formats. */}
                                {rabbitMQLogsData.data[apiCallIndex].item.denomination
                                  ? `${rabbitMQLogsData.data[apiCallIndex].item.productName} · ${rabbitMQLogsData.data[apiCallIndex].item.denomination}`
                                  : rabbitMQLogsData.data[apiCallIndex].item.productName}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", width: "30%" }}
                            >
                              IP Address
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].ipAddress || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Response Time
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].responseTime
                                ? `${rabbitMQLogsData.data[apiCallIndex].responseTime}ms`
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Status
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].status || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Supplier
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].supplier || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Timestamp
                            </TableCell>
                            <TableCell>
                              {rabbitMQLogsData.data[apiCallIndex].timestamp
                                ? dayjs(
                                    rabbitMQLogsData.data[apiCallIndex].timestamp,
                                  ).format("DD.MM.YYYY, HH:mm:ss")
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <NoApiCallRecorded />
                  )}
                </Box>
              )}
              {apiTab === 1 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    API Request
                  </Typography>
                  {rabbitMQLogsLoading ? (
                    <CircularProgress size={24} />
                  ) : rabbitMQLogsData?.data?.[apiCallIndex]?.apiRequest ? (
                    <Box
                      sx={{
                        backgroundColor: "#f5f5f5",
                        padding: 2,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "14px",
                        overflowX: "auto",
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {JSON.stringify(
                          rabbitMQLogsData.data[apiCallIndex].apiRequest,
                          null,
                          2,
                        )
                          .split("\n")
                          .map((line, index) => {
                            const keyMatch = line.match(/^(\s*)"([^"]+)":/);
                            if (keyMatch) {
                              const indent = keyMatch[1];
                              const key = keyMatch[2];
                              const rest = line.substring(keyMatch[0].length);
                              return (
                                <div key={index}>
                                  {indent}
                                  <span
                                    style={{
                                      color: "#0066cc",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    "{key}"
                                  </span>
                                  :
                                  <span style={{ color: "#008800" }}>
                                    {rest}
                                  </span>
                                </div>
                              );
                            }
                            return <div key={index}>{line}</div>;
                          })}
                      </pre>
                    </Box>
                  ) : (
                    <NoApiCallRecorded />
                  )}
                </Box>
              )}
              {apiTab === 2 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    API Response
                  </Typography>
                  {rabbitMQLogsLoading ? (
                    <CircularProgress size={24} />
                  ) : rabbitMQLogsData?.data?.[apiCallIndex]?.apiResponse ? (
                    <Box
                      sx={{
                        backgroundColor: "#f5f5f5",
                        padding: 2,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "14px",
                        overflowX: "auto",
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {JSON.stringify(
                          rabbitMQLogsData.data[apiCallIndex].apiResponse,
                          null,
                          2,
                        )
                          .split("\n")
                          .map((line, index) => {
                            const keyMatch = line.match(/^(\s*)"([^"]+)":/);
                            if (keyMatch) {
                              const indent = keyMatch[1];
                              const key = keyMatch[2];
                              const rest = line.substring(keyMatch[0].length);
                              return (
                                <div key={index}>
                                  {indent}
                                  <span
                                    style={{
                                      color: "#0066cc",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    "{key}"
                                  </span>
                                  :
                                  <span style={{ color: "#008800" }}>
                                    {rest}
                                  </span>
                                </div>
                              );
                            }
                            return <div key={index}>{line}</div>;
                          })}
                      </pre>
                    </Box>
                  ) : (
                    <NoApiCallRecorded />
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SalesReports;
